import Tag from '@cashew/tag';
import cn from 'classnames';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import Excel from '../../assets/Excel';
import Pdf from '../../assets/Pdf';
import type { RootState } from '../../reducers';
import { setSelectedMerchants } from '../../reducers/general';
import ProofOfPaymentModal from '../ProofOfPaymentModal';

const { priceFormat, dateFormat, api } = require('@cashew/utils');

const ns = 'settlement-item';

interface SettlementData {
  settlementId: string | number;
  cycleEndDate: string;
  cycleStartDate: string;
  processedDate?: string;
  status: string;
  balance?: number;
  currencyCode?: string;
  isBalance?: boolean;
  merchantId?: string | number;
  merchantCompanyName?: string;
  settlementType: string;
  paymentProofAvailable?: boolean;
}

interface Props {
  data: SettlementData;
}

// Static variable to keep global selection in this file
const globalSelectedMerchant: (string | number)[] = [];

export default ({
  data: {
    settlementId,
    cycleEndDate,
    cycleStartDate,
    processedDate,
    status,
    balance = 0,
    currencyCode = 'AED',
    isBalance = true,
    merchantId,
    merchantCompanyName,
    settlementType,
    paymentProofAvailable,
  },
}: Props) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);

  const dispatch = useDispatch();
  const selectedMerchants = useSelector(
    (state: RootState) => state.general.selectedMerchants
  );

  const isSelected = selectedMerchants.includes(settlementId);
  const isUnderReview = status === 'UNDER_REVIEW';
  const showProofButton = isUnderReview || paymentProofAvailable === true;

  return (
    <div className={ns}>
      <div>
        <input
          type="checkbox"
          className="form-checkbox rounded h-4 w-4 bg-black text-black accent-black"
          checked={isSelected}
          disabled={status === 'PROCESSED'}
          onChange={() => {
            let updated;
            if (!isSelected) {
              updated = [...selectedMerchants, settlementId];
            } else {
              updated = selectedMerchants.filter((id) => id !== settlementId);
            }
            dispatch(setSelectedMerchants(updated));
          }}
        />
      </div>
      <div className={`${ns}--dark`}>{settlementId}</div>
      <div className="flex flex-column justify-content-center align-items-center">
        <span className="flex justify-content-end align-items-center">
          {dateFormat({ date: cycleStartDate })}
          {} - {dateFormat({ date: cycleEndDate })}
        </span>

        {processedDate && (
          <span className="flex justify-content-end align-items-center">
            ({dateFormat({ date: processedDate })})
          </span>
        )}
      </div>

      {settlementType !== 'FINANCE_PARTNER' &&
        settlementType !== 'PAYFORT_CASHEW' && (
          <div className="flex justify-content-center align-items-center flex-column">
            <span className="flex justify-content-end align-items-center three-dots">
              {merchantId}
            </span>
            <span className="flex justify-content-end align-items-center three-dots">
              {merchantCompanyName}
            </span>
          </div>
        )}

      <div>
        <span
          className={cn(`${ns}--dark`, `${ns}__amount`, {
            [`${ns}--positive`]: isBalance && balance > 0,
            [`${ns}--negative`]: isBalance && balance < 0,
          })}
        >
          {priceFormat({
            currency: currencyCode,
            amount: balance,
            isBalance,
          })}
        </span>
        <Tag status={status}>
          <>{t(`status.${status}`)}</>
        </Tag>
      </div>
      {settlementType !== 'BANK_CASHEW' &&
        settlementType !== 'PAYFORT_CASHEW' && (
          <div className={`downloads ${isDownloading ? 'is-downloading' : ''}`}>
            <div className="loading" />
            <ul>
               {showProofButton && (
                <li>
                  <button
                    type="button"
                    className="proof-of-payment-btn px-1"
                    onClick={(event) => {
                      event.preventDefault();
                      setShowProofModal(true);
                    }}
                    title={t('settlements.proofOfPayment.title')}
                  >
                    <img
                      src="/images/money.png"
                      alt="Proof of Payment"
                      className="w-6 h-6"
                    />
                  </button>
                </li>
              )}
              <li>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    if (!isDownloading) {
                      setIsDownloading(true);
                      api
                        .get({
                          endpoint: `settlements/${settlementId}/excel`,
                        })
                        .then((response: any) => {
                          const a = document.createElement('a');
                          a.href = response.data.downloadUrl;
                          a.download = `${settlementId}.xlsx`;
                          document.body.appendChild(a);
                          a.click();
                          setIsDownloading(false);
                        })
                        .catch((error: any) => {
                          console.log(error);
                        });
                    }
                  }}
                >
                  <Excel />
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    if (!isDownloading) {
                      setIsDownloading(true);
                      api
                        .get({
                          endpoint: `settlements/${settlementId}/pdf`,
                        })
                        .then((response: any) => {
                          const a = document.createElement('a');
                          a.target = '_blank';
                          a.href = response.data.downloadUrl;
                          a.download = `${settlementId}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          setIsDownloading(false);
                        })
                        .catch((error: any) => {
                          console.log(error);
                        });
                    }
                  }}
                >
                  <Pdf />
                </button>
              </li>
             
            </ul>
          </div>
        )}
      <ProofOfPaymentModal
        isVisible={showProofModal}
        onClose={() => setShowProofModal(false)}
        settlementId={settlementId}
      />
    </div>
  );
};
